/**
 * Adapters from progression Items to the kit's slot / card (so screens don't map by hand).
 * Icons are the meta/ui slot glyphs tinted by rarity; +N is the Altar Forge temper steps.
 */
import type { Item, ItemSlot, TierSetDef } from '@keystone/progression';
import { tierSet } from '@keystone/progression';
import { slotGlyphSvg } from '../ui/glyphs';
import { SLOT_LABEL, itemLevel, itemName, itemTextLines } from '../ui/itemText';
import { type ItemCardData, type ItemSlotHandle, type ItemSlotOptions, itemCard, itemSlot } from './components';

/** Enhancement level shown as "+N" (successful temper steps). */
export const itemPlus = (item: Item): number => item.forge?.steps ?? 0;

/** Glyph element for a gear slot (tinted by the slot's rarity colour via CSS). */
export function slotIcon(slot: ItemSlot): SVGSVGElement {
  const g = slotGlyphSvg(slot);
  g.classList.add('km-glyph');
  return g;
}

/** Kit slot for an item (null → an empty slot showing the gear-slot glyph faintly). */
export function gearSlot(item: Item | null, o: ItemSlotOptions & { slot?: ItemSlot } = {}, parent?: HTMLElement): ItemSlotHandle {
  if (!item) return itemSlot({ ...o, empty: true, icon: o.slot ? slotIcon(o.slot) : undefined }, parent);
  return itemSlot({ rarity: item.rarity, icon: slotIcon(item.slot), plus: itemPlus(item), setPiece: !!item.setId, ...o }, parent);
}

/** Card data for an item; `equippedSetCount` = how many pieces of its set are worn. */
export function itemCardData(item: Item, o: { equippedSetCount?: number; hint?: string } = {}): ItemCardData {
  const lines = itemTextLines(item);
  const stats = lines.filter((l) => l.kind === 'stat').map((l) => {
    const m = /^\+(\d+) (.+?) \(([\d.]+%)\)/.exec(l.text);
    return m ? { label: m[2]!, value: `+${m[1]} (${m[3]})`, tone: l.tone === 'empowered' ? ('gold' as const) : undefined } : { label: l.text, value: '' };
  });
  const effects = lines.filter((l) => l.kind === 'effect' && l.tone !== 'set').map((l) => l.text);
  const set = item.setId ? tierSet(item.setId) : undefined;
  return {
    name: itemName(item),
    rarity: item.rarity,
    slotLabel: SLOT_LABEL[item.slot],
    ilvl: itemLevel(item),
    plus: itemPlus(item),
    icon: slotIcon(item.slot),
    stats,
    effects,
    set: set ? { name: set.name, have: o.equippedSetCount ?? 1, bonuses: setBonuses(set) } : undefined,
    hint: o.hint,
  };
}

/** Item tooltip card for an item. */
export function gearCard(item: Item, o: { equippedSetCount?: number; hint?: string } = {}, parent?: HTMLElement): HTMLElement {
  return itemCard(itemCardData(item, o), parent);
}

function setBonuses(set: TierSetDef): { pieces: number; text: string }[] {
  return Object.entries(set.text)
    .map(([k, t]) => ({ pieces: Number(k), text: `${t.name}: ${t.description}` }))
    .sort((a, b) => a.pieces - b.pieces);
}
